import toast from 'react-hot-toast'

import { useMutation, useQueryClient } from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'

import { useNotesQueryInvalidator } from '@/hooks/messages/useNotes'
import { useRemoveFromList, useUpdateCachedLoad } from '@/hooks/shipments'
import { loadKeys } from '@/hooks/shipments/useLoad'
import type { AwardLoadsMutationArgs } from '@/types'
import { shipperFetch } from '@/utils/shipperFetch'

import { useMutateRecentAccountData } from './hooks'

async function awardLoads(args: AwardLoadsMutationArgs): Promise<{
  isEdit: boolean
  isRetender: boolean
  loads: ListShipment[]
}> {
  const isEdit = args.isEdit === true
  const { isEdit: _, ...body } = args
  const endpoint = isEdit ? `/shipments/edit` : `/shipments/award`

  const result = await shipperFetch<ListShipment[]>(endpoint, {
    body,
    method: 'POST',
  })
  return {
    isEdit,
    isRetender: isEdit && 'retender' in body && !!body.retender,
    loads: result,
  }
}

export function useAwardLoads() {
  const { clear: clearRecentAccountData } = useMutateRecentAccountData()
  const queryClient = useQueryClient()
  const removeFromList = useRemoveFromList()
  const updateCachedLoad = useUpdateCachedLoad()
  const notesInvalidator = useNotesQueryInvalidator()
  return useMutation(awardLoads, {
    onSettled: (data, error) => {
      // Handle errors from callsite
      if (error || !data) return

      const { isEdit, isRetender, loads } = data
      const loadId = data.loads[0].loadId

      if (isRetender) {
        removeFromList({
          loadId,
          queryFilter: ['shipments', 'confirmed'],
        })
      }

      loads.forEach(updatedLoad => {
        updateCachedLoad({ updatedLoad })
        queryClient.invalidateQueries({
          queryKey: loadKeys.id(updatedLoad.loadId),
        })
      })

      // May require a rerate, only toast success if no longer pending
      if (loads[0].status !== 'pending') {
        toast.success(
          `${loads.length > 1 ? 'Loads' : 'Load'} ${
            isRetender ? 'retendered' : isEdit ? 'updated' : 'booked'
          } successfully.`,
        )
      }

      queryClient.invalidateQueries({ queryKey: ['customs-brokers'] })
      notesInvalidator(loads[0].loadId)
      clearRecentAccountData()
    },
  })
}
